/* =================================================================
   br-motion-parallax — shared scroll-parallax utility.

   Re-implements the &Fold donor's IX2 action a-104 ("Images Parallax")
   in vanilla JS for br-contact-hero and br-video-story. The utility was
   originally consolidated from several near-identical section-local copies:

     progress p = (viewportHeight - rect.top) / (viewportHeight + rect.height),
     clamped 0..1 (IX2 SCROLLING_IN_VIEW: 0% when the element's top enters
     the viewport bottom, 100% when its bottom leaves the viewport top),
     translateY from -15% to 0%, a small lerp standing in for IX2's
     smoothing (~50), driven by a rAF loop.

   USAGE CONTRACT

     window.brMotion.parallax({
       items: [ { measure: Element, targets: [Element, ...] }, ... ],
       from: -15,              // translateY at p=0  (default -15)
       to: 0,                  // translateY at p=1  (default 0)
       unit: '%' | 'px',       // '%' of the target's own height; 'px'
                               // resolves the same percentages against
                               // targets[0].offsetHeight (default '%')
       lerp: 0.12,             // per-frame easing toward the target
       epsilon: 0.02,          // settle threshold, in `unit`
       offscreenMargin: 0,     // px band beyond the viewport: past it the
                               // item snaps to its target and no longer
                               // keeps the loop alive.
                               // 0 = event-driven: the loop runs only
                               // until every item settles.
       deepScroll: false       // true = listen for scroll on `document`
                               // with capture, because the page scrolls
                               // inside .page-wrapper on desktop (>=990px,
                               // base.css) and window scroll never fires
                               // there. false = window scroll listener.
     })

   Each item carries ONE lerp state shared by all its targets, so a
   section that moves several images in lockstep passes them as one
   item's targets (contact-hero).

   GUARANTEES (same as the section-local copies)

   - prefers-reduced-motion: checked once at registration; nothing is
     measured, transformed or listened for. Targets keep their static
     CSS position.
   - No JS: no transform is ever applied; images hold their centered
     CSS position.
   - Idempotent: re-executing this file is a no-op (global guard);
     re-registering the same measure element is skipped (theme-editor
     section re-renders re-execute section scripts). Registrations whose
     elements leave the DOM are pruned and their listeners removed.

   No globals beyond window.brMotion.
   ================================================================= */
(function () {
  window.brMotion = window.brMotion || {};
  if (window.brMotion.parallax) return; /* already loaded by another section */

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  window.brMotion.parallax = function (config) {
    if (REDUCED.matches) return;

    var options = {
      from: config.from != null ? config.from : -15,
      to: config.to != null ? config.to : 0,
      unit: config.unit || '%',
      lerp: config.lerp != null ? config.lerp : 0.12,
      epsilon: config.epsilon != null ? config.epsilon : 0.02,
      offscreenMargin: config.offscreenMargin || 0,
      deepScroll: !!config.deepScroll,
    };

    var items = [];
    (config.items || []).forEach(function (entry) {
      if (!entry.measure || !entry.targets || !entry.targets.length) return;
      if (entry.measure.__brMotionParallax) return; /* theme-editor re-execution */
      entry.measure.__brMotionParallax = true;
      items.push({ measure: entry.measure, targets: entry.targets, current: null });
    });
    if (!items.length) return;

    var rafId = null;
    var alive = true;

    function targetFor(item, rect, vh) {
      var p = (vh - rect.top) / (vh + rect.height);
      p = Math.max(0, Math.min(1, p));
      var percent = options.from + (options.to - options.from) * p;
      if (options.unit === 'px') {
        return (percent / 100) * item.targets[0].offsetHeight;
      }
      return percent;
    }

    function write(item) {
      var value = 'translateY(' + item.current.toFixed(3) + options.unit + ')';
      for (var i = 0; i < item.targets.length; i++) {
        item.targets[i].style.transform = value;
      }
    }

    function frame() {
      rafId = null;
      var vh = window.innerHeight;
      var needsMore = false;
      items.forEach(function (item) {
        if (!item.measure.isConnected) return;
        var rect = item.measure.getBoundingClientRect();
        var target = targetFor(item, rect, vh);
        if (
          options.offscreenMargin > 0 &&
          (rect.bottom < -options.offscreenMargin || rect.top > vh + options.offscreenMargin)
        ) {
          /* Beyond the band: snap to the target and stay out of the
             keep-alive decision (invisible off-screen either way). */
          if (item.current !== target) {
            item.current = target;
            write(item);
          }
          return;
        }
        if (item.current === null) item.current = target; /* first eval: snap, no intro drift */
        item.current += (target - item.current) * options.lerp;
        if (Math.abs(target - item.current) < options.epsilon) item.current = target;
        write(item);
        if (options.offscreenMargin > 0) {
          needsMore = true; /* in-band: keep polling while visible, like the donor port */
        } else if (item.current !== target) {
          needsMore = true;
        }
      });
      if (needsMore && alive) rafId = window.requestAnimationFrame(frame);
    }

    function teardown() {
      alive = false;
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
      if (options.deepScroll) {
        document.removeEventListener('scroll', kick, { capture: true });
      } else {
        window.removeEventListener('scroll', kick);
      }
      window.removeEventListener('resize', kick);
    }

    function kick() {
      if (!alive) return;
      items = items.filter(function (item) {
        return item.measure.isConnected;
      });
      if (!items.length) {
        teardown(); /* section left the DOM (theme editor) */
        return;
      }
      if (rafId === null) rafId = window.requestAnimationFrame(frame);
    }

    /* The page scrolls inside .page-wrapper on desktop and on the window
       on mobile — deepScroll listens on document with capture, which
       catches the wrapper's scroll either way. */
    if (options.deepScroll) {
      document.addEventListener('scroll', kick, { capture: true, passive: true });
    } else {
      window.addEventListener('scroll', kick, { passive: true });
    }
    window.addEventListener('resize', kick, { passive: true });

    frame(); /* initial measure: snap to the scroll position, then keep
                the loop alive only as the mode requires */
  };
})();
